import { create, useStore } from 'zustand';
import { createContext, useContext } from 'react';
import { immer } from 'zustand/middleware/immer';
import {
  getSessionByContactGet,
  emailGuidePost,
  sessionUnreadPost,
  emailReadPost,
  readPost,
} from '@/services/swagger/easourcing/SessionService';
import type { IApiSessionInfoResp, IApiSessionEmailGuideResp } from '@/services/swagger/easourcing/models';

/**
 * Supplier Agent 状态定义
 */
interface SupplierAgentState {
  /** 邮件预览 Dialog 是否打开 */
  emailPreviewDialogOpen: boolean;
  /** 未读消息数量 */
  unreadCount: number;
  /** 当前联系人 ID */
  contactId: string | null;
  /** 当前会话信息 */
  sessionInfo: IApiSessionInfoResp | null;
  /** 会话信息加载状态 */
  sessionLoading: boolean;
  /** 会话信息加载错误 */
  sessionError: string | null;
  /** 邮件编辑指引信息 */
  emailGuideInfo: IApiSessionEmailGuideResp | null;
  /** 邮件编辑指引加载状态 */
  emailGuideLoading: boolean;
  /** 未读统计加载状态 */
  sessionUnreadLoading: boolean;
  /** 初始化加载状态 */
  initializing: boolean;
}

/**
 * Supplier Agent Actions 定义（仅对外暴露必要方法）
 */
interface SupplierAgentActions {
  /** 根据 contactId 获取会话信息 */
  fetchSessionByContact: (contactId: string) => Promise<void>;
  /** 查询是否需要展示邮件编辑指引 */
  fetchEmailGuide: (sessionId: string) => Promise<void>;
  /** 获取 session 未读统计，结果同步到 unreadCount */
  fetchSessionUnread: (params: { allUnread?: boolean; sessionIdList?: string[] }) => Promise<void>;
  /**
   * 初始化方法，在组件 mounted 时调用
   * 依次执行：1.获取 session 2.获取未读消息 3.获取邮件编辑指引
   */
  initialize: () => Promise<void>;
  /**
   * 标记邮件消息已读
   * 1. 调用 email-read 接口
   * 2. 乐观更新本地未读数量
   * 3. 重新调用未读统计接口，以接口数据为最终准确值
   * 4. 清空邮件编辑指引信息
   */
  markEmailRead: (params: { messageId?: string; sessionId?: string }) => Promise<void>;
  /**
   * 清理当前 session 的所有未读消息
   * 1. 调用 /session/read 接口标记全部已读
   * 2. 更新本地未读数量为 0
   */
  clearAllUnread: () => Promise<void>;
  setSupplierAgentState: (state: Partial<SupplierAgentState>) => void;
  getSupplierAgentState: () => SupplierAgentState;
  openEmailPreviewDialog: () => void;
  handleDismissClick: () => Promise<void>;
  /**
   *
   * 刷新当前 session 的未读统计和邮件编辑指引
   * 1. 调用未读统计接口，更新本地未读数量
   * 2. 调用邮件编辑指引接口，更新本地邮件编辑指引信息
   */
  refreshUnreadAndEmailGuide: () => Promise<void>;
}

/**
 * Supplier Agent 状态管理 store 接口定义（Context 作用域版本）
 */
type SupplierAgentStoreState = SupplierAgentState & SupplierAgentActions;

/**
 * 创建 store 时支持传入的初始参数
 */
interface CreateSupplierAgentStoreOptions {
  /** 初始联系人 ID */
  contactId?: string | null;
}

/**
 * 获取初始状态
 */
const getInitialState = (options?: CreateSupplierAgentStoreOptions): SupplierAgentState => ({
  emailPreviewDialogOpen: false,
  unreadCount: 0,
  contactId: options?.contactId ?? null,
  sessionInfo: null,
  sessionLoading: false,
  sessionError: null,
  emailGuideInfo: null,
  emailGuideLoading: false,
  sessionUnreadLoading: false,
  initializing: false,
});

/**
 * 创建 store 状态管理 store（Context 作用域版本）
 * 每次调用都会创建一个新的 store 实例，适合通过 Context 在组件树作用域内共享
 * @param options - 可选初始参数，支持传入 contactId
 * @returns 新的 store 实例
 */
export const createSupplierAgentStore = (options?: CreateSupplierAgentStoreOptions) => {
  return create<SupplierAgentStoreState>()(
    immer((set, get) => ({
      ...getInitialState(options),
      fetchSessionByContact: async (contactId: string) => {
        set({
          sessionLoading: true,
          sessionError: null,
        });
        try {
          const result = await getSessionByContactGet({ contactId });
          set({
            sessionInfo: result?.data ?? null,
            sessionLoading: false,
          });
        } catch (error) {
          set({
            sessionError: error instanceof Error ? error.message : '获取会话信息失败',
            sessionLoading: false,
          });
        }
      },
      fetchEmailGuide: async (sessionId: string) => {
        set({
          emailGuideLoading: true,
        });
        try {
          const result = await emailGuidePost({ sessionId });
          console.log(1111, 'emailGuidePost', result);
          set({
            emailGuideInfo: result?.data ?? null,
            emailGuideLoading: false,
          });
        } catch {
          set({
            emailGuideLoading: false,
          });
        }
      },
      fetchSessionUnread: async (params: { allUnread?: boolean; sessionIdList?: string[] }) => {
        set({
          sessionUnreadLoading: true,
        });
        try {
          const result = await sessionUnreadPost(params);
          const resData = result?.data;
          const currentSession = resData?.sessionUnreadList?.find(
            item => item.sessionId === get().sessionInfo?.sessionDto?.sessionId
          );
          set({
            sessionUnreadLoading: false,
            // 以接口返回的总未读数同步更新 unreadCount
            unreadCount: currentSession?.unreadNum ?? 0,
          });
        } catch {
          set({
            sessionUnreadLoading: false,
          });
        }
      },
      initialize: async () => {
        const { contactId, fetchSessionByContact, refreshUnreadAndEmailGuide } = get();

        if (!contactId) {
          return;
        }

        set({ initializing: true });

        try {
          // 第一步：根据 contactId 获取 session 信息
          await fetchSessionByContact(contactId);

          // 第二步：刷新未读统计和邮件编辑指引
          await refreshUnreadAndEmailGuide();
        } finally {
          set({ initializing: false });
        }
      },
      refreshUnreadAndEmailGuide: async () => {
        const sessionId = get().sessionInfo?.sessionDto?.sessionId;
        // 并行调用未读统计和邮件编辑指引接口
        await Promise.all([
          get().fetchSessionUnread({ sessionIdList: sessionId ? [sessionId] : [] }),
          sessionId ? get().fetchEmailGuide(sessionId) : Promise.resolve(),
        ]);
      },
      markEmailRead: async (params: { messageId?: string; sessionId?: string }) => {
        try {
          // 第一步：调用标记已读接口
          await emailReadPost(params);

          // 第二步：乐观更新本地未读数量，立即响应 UI
          set(state => {
            if (state.unreadCount > 0) {
              state.unreadCount -= 1;
            }
          });

          // 第三步：重新请求未读统计接口，以接口数据为最终准确值
          await get().refreshUnreadAndEmailGuide();
          // 第四步：清空邮件编辑指引信息
          set({ emailGuideInfo: null });
        } catch (error) {
          console.error('标记邮件已读失败：', error);
          throw error;
        }
      },

      clearAllUnread: async () => {
        const sessionId = get().sessionInfo?.sessionDto?.sessionId;
        try {
          // 第一步：乐观更新本地未读数量为 0, 并清空邮件编辑指引信息
          set({
            unreadCount: 0,
            emailGuideInfo: null,
            emailGuideLoading: false,
          });
          // 第二步：调用标记全部已读接口
          await readPost({ sessionId });

          // 第三步：重新请求未读统计接口，以接口数据为最终准确值
          await get().refreshUnreadAndEmailGuide();
        } catch (error) {
          console.error('清理所有未读消息失败：', error);
          throw error;
        }
      },
      handleDismissClick: async () => {
        const { emailGuideInfo, sessionInfo, markEmailRead } = get();
        const messageId = emailGuideInfo?.messageId;
        const sessionId = sessionInfo?.sessionDto?.sessionId;

        // 第一步：立即关闭指引
        set({ emailGuideInfo: null });

        // 第二步：调用 markEmailRead 标记邮件已读
        await markEmailRead({ messageId, sessionId });
      },
      openEmailPreviewDialog: () => set({ emailPreviewDialogOpen: true }),
      setSupplierAgentState: (state: Partial<SupplierAgentState>) => set(state),
      getSupplierAgentState: () => get(),
    }))
  );
};

/**
 * store 状态管理 store 类型定义
 */
type SupplierAgentStore = ReturnType<typeof createSupplierAgentStore>;

/**
 * store 状态管理 store 上下文定义
 * 用于在 React 组件树中传递 store 实例
 */
export const SupplierAgentStoreContext = createContext<SupplierAgentStore | null>(null);

/**
 * store 状态管理 store Provider 组件
 * 用于包裹需要访问 store 的组件树
 */
export const SupplierAgentStoreProvider = SupplierAgentStoreContext.Provider;

/**
 * store 状态管理 store 选择器 hook（Context 作用域版本）
 * 用于在组件中选择和使用 Context 中的 store 状态
 * @param selector - 状态选择器函数，用于选择需要的部分状态
 * @returns 选择的状态
 * @throws 当没有在 Context Provider 中时抛出错误
 */
export const useSupplierAgentStore = <T>(selector: (state: SupplierAgentStoreState) => T): T => {
  const store = useContext(SupplierAgentStoreContext);
  if (!store) {
    throw new Error('Missing SupplierAgentStoreContext.Provider in the tree');
  }
  return useStore(store, selector);
};

/**
 * 获取整个 store 实例（Context 作用域版本）
 * 用于在组件中获取当前的 store 实例
 * @returns 当前 Context 中的 store 实例
 * @throws 当没有在 Context Provider 中时抛出错误
 */
export const useSupplierAgentStoreInstance = (): SupplierAgentStore => {
  const store = useContext(SupplierAgentStoreContext);
  if (!store) {
    throw new Error('Missing SupplierAgentStoreContext.Provider in the tree');
  }
  return store;
};
